import { BUILTIN_SHORTCUTS } from "../domain/views";
import {
  ACCENT_COLORS,
  THEME_PREFERENCES,
  type AccentColor,
  type Settings,
  type ThemePreference,
} from "../state/settings";
import { Dialog } from "./Dialog";

interface SettingsDialogProps {
  settings: Settings;
  serverLabel: string;
  onChange(update: (current: Settings) => Settings): void;
  onSignOut(): void;
  onClose(): void;
}

const THEME_LABELS: Record<ThemePreference, string> = { system: "System", light: "Light", dark: "Dark" };

const ACCENT_LABELS: Record<AccentColor, string> = {
  blue: "Blue",
  violet: "Violet",
  green: "Green",
  orange: "Orange",
  red: "Red",
  pink: "Pink",
};

export function SettingsDialog({ settings, serverLabel, onChange, onSignOut, onClose }: SettingsDialogProps) {
  return (
    <Dialog title="Settings" onClose={onClose}>
      <h3 className="settings-heading">Appearance</h3>
      <fieldset className="settings-group">
        <legend>Theme</legend>
        <div className="choice-row">
          {THEME_PREFERENCES.map((theme) => (
            <label key={theme} className="choice">
              <input
                type="radio"
                name="theme"
                checked={settings.theme === theme}
                onChange={() => onChange((current) => ({ ...current, theme }))}
              />
              {THEME_LABELS[theme]}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="settings-group">
        <legend>Accent color</legend>
        <div className="choice-row">
          {ACCENT_COLORS.map((accent) => (
            <label key={accent} className="swatch-choice">
              <input
                type="radio"
                name="accent"
                aria-label={ACCENT_LABELS[accent]}
                checked={settings.accent === accent}
                onChange={() => onChange((current) => ({ ...current, accent }))}
              />
              <span className="swatch" data-accent={accent} aria-hidden="true" />
            </label>
          ))}
        </div>
      </fieldset>

      <h3 className="settings-heading">Startup</h3>
      <div className="field">
        <label htmlFor="startup-shortcut">Open on launch</label>
        <select
          id="startup-shortcut"
          className="text-input"
          value={settings.startupShortcut}
          onChange={(event) => {
            const chosen = BUILTIN_SHORTCUTS.find((shortcut) => shortcut.id === event.target.value);
            if (chosen !== undefined) onChange((current) => ({ ...current, startupShortcut: chosen.id }));
          }}
        >
          {BUILTIN_SHORTCUTS.map((shortcut) => (
            <option key={shortcut.id} value={shortcut.id}>
              {shortcut.title}
            </option>
          ))}
        </select>
        <p className="field-hint">These settings belong to this device.</p>
      </div>

      <h3 className="settings-heading">Account</h3>
      <p>Connected to {serverLabel}.</p>

      <div className="dialog-actions">
        <button type="button" className="button" onClick={onClose}>
          Close
        </button>
        <button type="button" className="button danger" onClick={onSignOut}>
          Sign out and change server
        </button>
      </div>
    </Dialog>
  );
}
